import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SearchOutlined } from "@mui/icons-material";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import PaginationControls from "components/fileStorage/PaginationControls";
import { CALIBRATE_COLORS } from "components/hoc/Theme";
import SecureExchangeHeader from "components/secureExchange/SecureExchangeHeader";
import SecureExchangeShell from "components/secureExchange/SecureExchangeShell";
import {
  CLIENT_COLUMNS,
  CLIENT_LIST_PAGE_SIZE,
  DEFAULT_SORT,
  type SortState,
  activityDate,
  newFilesLabel,
  nextSort,
  pageCount,
  toOrdering,
} from "components/secureExchange/clientListModel";
import { ClientIcon, ExchangeIcon } from "components/secureExchange/icons";
import { useDebouncedValue } from "components/secureExchange/useDebouncedValue";
import {
  type SecureClientColumn,
  type SecureClientRow,
  useGetSecureClientListQuery,
} from "features/secureUploads/secureUploads";
import useResponsive from "hooks/useResponsive";
import { useViewAs } from "hooks/useViewAs";

const LINK_COLOR = CALIBRATE_COLORS.iconContainerFill;
const TITLE = "Secure Exchange";
const SUBTITLE = "Share files securely across clients.";

function NewFiles({ count }: { count: number }) {
  const label = newFilesLabel(count);
  if (!label) return <>-</>;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        color: LINK_COLOR,
        bgcolor: alpha(LINK_COLOR, 0.12),
      }}
    />
  );
}

function ClientName({ row, to }: { row: SecureClientRow; to: string }) {
  return (
    <MuiLink
      component={Link}
      to={to}
      underline="hover"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        color: LINK_COLOR,
        fontWeight: 600,
        minWidth: 0,
      }}
    >
      <ClientIcon sx={{ fontSize: 20, flexShrink: 0 }} />
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {row.name}
      </Box>
    </MuiLink>
  );
}

/** A client user has one client: the page sends them straight to it. */
function ClientUserLanding() {
  const { href } = useViewAs();
  const { data, isLoading, isError } = useGetSecureClientListQuery({
    page_size: 1,
  });
  const own = data?.results[0];
  if (own)
    return <Navigate replace to={href(`/secure-exchange/clients/${own.id}`)} />;
  return (
    <SecureExchangeShell
      title={TITLE}
      header={
        <SecureExchangeHeader
          icon={<ExchangeIcon />}
          title={TITLE}
          loading={isLoading}
        />
      }
    >
      {isLoading ? null : (
        <Typography sx={{ p: 3 }} color="text.secondary">
          {isError
            ? "The Secure Exchange could not be opened. Try again in a moment."
            : "The Secure Exchange is not set up for your company yet."}
        </Typography>
      )}
    </SecureExchangeShell>
  );
}

function ClientList() {
  const { href } = useViewAs();
  const { isCardMode } = useResponsive();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError } = useGetSecureClientListQuery({
    search: search || undefined,
    ordering: toOrdering(sort),
    page,
    page_size: CLIENT_LIST_PAGE_SIZE,
  });
  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const clientPath = (row: SecureClientRow) =>
    href(`/secure-exchange/clients/${row.id}`);

  const sortBy = (column: SecureClientColumn) => {
    setSort((current) => nextSort(current, column));
    setPage(1);
  };

  const empty = search
    ? `No clients match "${search}".`
    : "No clients use the Secure Exchange yet.";

  const table = (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Table stickyHeader size="small" aria-label="Clients">
        <TableHead>
          <TableRow>
            {CLIENT_COLUMNS.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sortDirection={
                  sort.column === column.key ? sort.direction : false
                }
                sx={{ bgcolor: "grey.100", whiteSpace: "nowrap" }}
              >
                <TableSortLabel
                  active={sort.column === column.key}
                  direction={
                    sort.column === column.key ? sort.direction : "asc"
                  }
                  onClick={() => sortBy(column.key)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? Array.from({ length: 4 }, (_, index) => (
                <TableRow key={index}>
                  {CLIENT_COLUMNS.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <ClientName row={row} to={clientPath(row)} />
                  </TableCell>
                  <TableCell align="center">{row.open_projects}</TableCell>
                  <TableCell align="center">
                    <NewFiles count={row.new_files} />
                  </TableCell>
                  <TableCell align="center">{row.contributors}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {activityDate(row.latest_activity)}
                  </TableCell>
                </TableRow>
              ))}
          {!isLoading && rows.length === 0 && !isError ? (
            <TableRow>
              <TableCell colSpan={CLIENT_COLUMNS.length} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  {empty}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const cards = (
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, pb: 1.5 }}>
      {isLoading ? (
        <Skeleton variant="rounded" height={96} />
      ) : rows.length === 0 && !isError ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {empty}
        </Typography>
      ) : (
        rows.map((row) => (
          <Box
            key={row.id}
            sx={{
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "grid",
              gap: 0.5,
            }}
          >
            <ClientName row={row} to={clientPath(row)} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 2,
                rowGap: 0.5,
                fontSize: 13,
                color: CALIBRATE_COLORS.secondaryText,
              }}
            >
              <span>Open projects: {row.open_projects}</span>
              <span>Contributors: {row.contributors}</span>
              <Box component="span" sx={{ display: "flex", gap: 0.5 }}>
                New files: <NewFiles count={row.new_files} />
              </Box>
              <span>Latest: {activityDate(row.latest_activity)}</span>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <SecureExchangeShell
      title={TITLE}
      header={
        <SecureExchangeHeader
          icon={<ExchangeIcon />}
          title={TITLE}
          subtitle={SUBTITLE}
          loading={isLoading}
          counters={[
            { label: "Clients", value: data?.totals.clients },
            { label: "Open Projects", value: data?.totals.open_projects },
          ]}
        />
      }
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setPage(1);
          }}
          placeholder="Search Clients"
          inputProps={{ "aria-label": "Search clients" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
        {isCardMode ? (
          <Select
            size="small"
            value={toOrdering(sort)}
            onChange={(event) => {
              const ordering = String(event.target.value);
              setSort({
                column: ordering.replace(/^-/, "") as SecureClientColumn,
                direction: ordering.startsWith("-") ? "desc" : "asc",
              });
              setPage(1);
            }}
            inputProps={{ "aria-label": "Sort clients" }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="name">Name A-Z</MenuItem>
            <MenuItem value="-latest_activity">Latest activity</MenuItem>
            <MenuItem value="-new_files">Most new files</MenuItem>
            <MenuItem value="-open_projects">Most open projects</MenuItem>
          </Select>
        ) : null}
      </Box>
      {isError ? (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          The client list could not be loaded. Try again in a moment.
        </Alert>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: isFetching && !isLoading ? 0.6 : 1,
          transition: "opacity 120ms",
        }}
      >
        {isCardMode ? cards : table}
      </Box>
      {total > 0 ? (
        <PaginationControls
          currentLimit={CLIENT_LIST_PAGE_SIZE}
          onLimitChange={() => undefined}
          totalItems={total}
          currentPage={page}
          totalPages={pageCount(total)}
          onPageChange={setPage}
          showLimitSelector={false}
          size="small"
          hasNextPage={page < pageCount(total)}
        />
      ) : null}
    </SecureExchangeShell>
  );
}

/** `/secure-exchange`: staff land on the clients they work with; a client user is
 * sent to their own client. */
export default function SecureExchangeClientListPage() {
  const { profile } = useViewAs();
  if (profile?.user_type === "client") return <ClientUserLanding />;
  return <ClientList />;
}
